import { Component, OnInit } from '@angular/core';
import { DataService } from 'src/app/shared/network/data.service';
import { ActivatedRoute, Router } from '@angular/router';

import { ConfidenceModel } from '../shared/project/cards/confidence-model';
import { DriverSelectionAlgorithm } from '../shared/project/cards/driver-selection-algorithm';
import { WaterfallItem } from './waterfall-item';
import { ProjectService } from '../shared/project/project.service';
import { Card } from '../shared/project/cards/card';
import { DriverType } from '../shared/project/driver-types/driver-type';
import { BenchmarkDiagramPoint, BenchmarkDiagramSeries } from '../shared/benchmarks/benchmarks-model';
import { RoundingHelper, RoundingSettings } from '../shared/rounding/rounding-helper';
import { CardHelper } from './cardHelper';
import { FancyNumberPipe } from '../shared/directives/fancy-number.pipe';

@Component({
    templateUrl: './confidence.component.html',
    styleUrls: ['./cards.component.scss']
})
export class ConfidenceComponent implements OnInit {
    DriverType: typeof DriverType = DriverType;   //hack for enum to be available in the template
    DriverSelectionAlgorithm: typeof DriverSelectionAlgorithm = DriverSelectionAlgorithm;

    loading = false;

    public projectId: string;
    private viewId: string;
    private tabId: string;
    private cardIndex: number;
    private cardsCount: number;

    public returnTabId: string;
    public returnViewId: string;

    public model: ConfidenceModel;
    waterfallItems: WaterfallItem[] = [];
    public showMoreCard: boolean = true;

    oldOutcomeTop: number = 0;
    newOutcomeTop: number = 0;
    blockHeight: number = 42;

    public outcomeBeforeCardTop: number;

    seqMode: boolean = true;
    showNavigator: boolean = false;
    showAlgoSelection: boolean = false;
    selectedAlgorithm: DriverSelectionAlgorithm;
    showWaterfall: boolean = true;

    //diagrams in cards
    public pointWidth = 50;
    public viewBoxWidth = 100;
    public viewBoxHeight = 40;

    cardOutcomeCtx = FancyNumberPipe.createDefaultContext();
    cardPercCtx = {
        roundTo: 1,
        scale: undefined,
        showPlusForPositive: true,
        use2stepRounding: false,
    };

    constructor(private dataService: DataService, private activatedRoute: ActivatedRoute, private router: Router,
        private projectService: ProjectService) {
    }

    ngOnInit(): void {
        this.loading = false;
        this.activatedRoute.queryParams.subscribe((queryParams: any) => {
            let projectId = queryParams.projectId;
            let viewId = queryParams.viewId;
            let tabId = queryParams.tabId;
            let cardIndex = queryParams.cardIndex;
            let cardsCount = queryParams.cardsCount ? queryParams.cardsCount : 4;
            if (projectId && cardIndex != undefined) {
                this.projectId = projectId;
                this.viewId = viewId;
                this.tabId = tabId;
                this.cardIndex = cardIndex;
                this.cardsCount = cardsCount;
                this.returnViewId = history.state.returnViewId;
                this.returnTabId = history.state.returnTabId;
                this.projectService.setProjectId(projectId);
                this.projectService.getConfidence(viewId, tabId, cardIndex, cardsCount, null).then(res => {
                    this.loadModel(res);
                });
            } else {
                this.router.navigateByUrl(`cards?projectId=${projectId}`);
            }
        });
    }

    private loadModel(confidenceModel: ConfidenceModel): void {
        this.model = confidenceModel;
        if (this.cardsCount > this.model.cards.length) {
            this.showMoreCard = false;
        }
        this.selectedAlgorithm = confidenceModel.algorithm;
        this.loadWaterfallItems(confidenceModel.selectedCard, confidenceModel.cards);
        this.showAlgoSelection = false;
        
        CardHelper.initCardDiagram(this.model.selectedCard, this.model.selectedDiagramSeriesName, this.model.oldControlValue, this.model.newControlValue, true, this.viewBoxWidth, this.viewBoxHeight);
        for (let i = 0; i < this.model.cards.length; ++i) {
            if (this.model.cards[i].altViewSeq.diagramSeries && this.model.cards[i].altViewSeq.diagramSeries.length) {
                CardHelper.initCardDiagram(this.model.cards[i], this.model.selectedDiagramSeriesName, this.model.oldControlValue, this.model.newControlValue, true, this.viewBoxWidth, this.viewBoxHeight);
            }
        }
        CardHelper.calcCardsFancyNumContexts([ this.model.selectedCard ]);
        CardHelper.calcCardsFancyNumContexts(this.model.cards);
        this.cardOutcomeCtx = CardHelper.calcOutcomeFancyNumContext(this.model.cards, [ this.model.outcomeBeforeCard, this.model.selectedCard.seqDriverValue, this.model.selectedCard.absDriverValue ]);
        this.cardOutcomeCtx.showPlusForPositive = true;
    }

    public setSeqMode(val: boolean): void {
        this.seqMode = val;
        this.loadWaterfallItems(this.model.selectedCard, this.model.cards);
    }

    public algoIconClick(algo: DriverSelectionAlgorithm) {
        if (!this.showAlgoSelection) {
            this.showAlgoSelection = true;
            return;
        }
        this.selectedAlgorithm = algo;
    }

    public async saveAlgoSettings() {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let res = await this.projectService.getConfidence(this.viewId, this.tabId, this.cardIndex, this.cardsCount, this.selectedAlgorithm);
            this.loadModel(res);
        } finally {
            this.loading = false;
        }
    }

    public async cancelAlgoSettings () {
        this.showAlgoSelection = false;
    }

    dragStarted(event: any) {
        this.showWaterfall = false;
    }

    async dropped(event: any) {
        this.showWaterfall = true;
        if (event.previousIndex == event.currentIndex) {
            return;
        }
        let dragged = this.model.cards[event.previousIndex];
        this.model.cards.splice(event.previousIndex, 1);
        this.model.cards.splice(event.currentIndex, 0, dragged);
        this.loadWaterfallItems(this.model.selectedCard, this.model.cards);
    }

    public async removeCard(i: number) {
        this.model.cards.splice(i, 1);
    }

    public async addCardBeforeConfidenceCheckedCard(i: number) {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            await this.projectService.addCardBeforeConfidenceCheckedCard(this.viewId, this.tabId, this.cardIndex,
                this.model.cards[i].allDimensions, this.model.cards[i].driverType, this.model.cards[i].formulaComponentIndex);
           
            this.router.navigateByUrl(`/cards?projectId=${this.projectId}`);
        } finally {
            this.loading = false;
        }
    }

    public async loadMoreCards() {
        try {
            this.loading = true;
            this.cardsCount += 4;
            let res = await this.projectService.getConfidence(this.viewId, this.tabId, this.cardIndex, this.cardsCount, this.selectedAlgorithm);
            this.loadModel(res);
        } finally {
            this.loading = false;
        }
    }

    public setDimLock(dimIndex: number, val: boolean) {
        
    }
    
    public async replaceCard (i: number) {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            await this.projectService.replaceCard(this.viewId, this.tabId, this.cardIndex,
                this.model.cards[i].allDimensions, this.model.cards[i].driverType, this.model.cards[i].formulaComponentIndex);
           
            this.router.navigateByUrl(`/cards?projectId=${this.projectId}`);
        } finally {
            this.loading = false;
        }
    }

    private loadWaterfallItems(selectedCard: Card, cards: Card[]) {
        const totalHeight = this.blockHeight;
        let waterfallItems = new Array<WaterfallItem>(cards.length);
        let seqMax = Math.abs(selectedCard.seqChartDiff);
        let absMax = Math.abs(selectedCard.absChartDiff);
        for (let i = 0; i < cards.length; ++i) {
            let seqDiff = cards[i].seqChartDiff;
            let absDiff = cards[i].absChartDiff;
            if (seqMax < Math.abs(seqDiff)) {
                seqMax = Math.abs(seqDiff);
            }
            if (absMax < Math.abs(absDiff)) {
                absMax = Math.abs(absDiff);
            }
        }
        const topBorder = 10;
        this.outcomeBeforeCardTop = totalHeight / 2;

        waterfallItems[0] = this.createWaterfallItem(selectedCard, seqMax, absMax, totalHeight);
        for (let i = 0; i < cards.length; ++i) {
             waterfallItems[i + 1] = this.createWaterfallItem(cards[i], seqMax, absMax, totalHeight);
        }

        this.waterfallItems = waterfallItems;
    }

    private createWaterfallItem(card: Card, seqMax: number, absMax: number, totalHeight: number) {
        let item = new WaterfallItem();
        item.seqHeight = Math.abs(card.seqChartDiff / seqMax * totalHeight / 2);
        item.absHeight = Math.abs(card.absChartDiff / absMax * totalHeight / 2);
        item.seqTop = card.seqChartDiff > 0 ? totalHeight / 2 - item.seqHeight : totalHeight / 2;
        item.seqColor = card.seqChartColorSeqMode;
        item.absColor = card.absChartColor;
        this.calcAbsTop(item, card);
        return item;
    }
    
    private calcAbsTop(item: WaterfallItem, card: Card): void {
        //abs top is relative to seq top;
        if (card.absChartDiff < 0) {
            item.absTop = card.seqChartDiff < 0 
                ? -1
                : item.seqHeight - 1;
        } else {
            item.absTop = card.seqChartDiff < 0 
                ? - item.absHeight - 1
                : item.seqHeight - item.absHeight - 1;
        }
    }

    public cardMouseEnter(card: Card) {
        card.showAltView = true;
    }
    public cardMouseLeave(card: Card) {
        card.showAltView = false;
    }

    public selectSeries(ser: BenchmarkDiagramSeries) {
        for (let i = 0; i < this.model.cards.length; ++i) {
            let card = this.model.cards[i];
            card.selectedDiagram = card.altViewSeq.diagramSeries.find(d => d.name == ser.name);
            card.diagramComboOpened = false;
        }
    }

}
